import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Check, Truck, ExternalLink, ClipboardCheck, Factory, ShieldCheck, Package, Boxes, Home, FileCheck2 } from "lucide-react";

const STAGE_ICONS = {
  "Order Confirmed": ClipboardCheck,
  "Production Started": Factory,
  "Quality Check": ShieldCheck,
  "Packing": Package,
  "Ready for Dispatch": Boxes,
  "Dispatched": Truck,
  "Delivered": Home,
};

const Detail = ({ l, v }) => (
  <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
    <p className="text-[10px] uppercase tracking-wider text-slate-400">{l}</p>
    <p className="text-sm font-semibold text-slate-900 mt-0.5">{v || "—"}</p>
  </div>
);

export default function PortalDispatch() {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/portal/orders").then((r) => setList(r.data)).catch(() => setList([])); }, []);
  const viewPod = async (fid) => {
    try {
      const r = await api.get(`/portal/pod/${fid}`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { /* ignore */ }
  };

  const tracked = (list || []).filter((so) => so.status !== "Cancelled");

  return (
    <div className="space-y-4" data-testid="portal-dispatch">
      <h1 className="text-2xl font-display font-bold text-slate-900 tracking-tight">Dispatch & Tracking</h1>
      {tracked.map((so) => {
        const isDispatched = Boolean(so.dispatch) || ["Dispatched", "Delivered"].includes(so.status);
        return (
        <div key={so.id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`pd-card-${so.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm flex items-center gap-3">
              <span className="font-mono font-semibold text-slate-900">{so.number}</span>
              <span className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3.5 py-1.5 border ${
                so.stage_index === so.timeline.length - 1 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-[#FC5F5F]/10 text-[#C94A4A] border-[#FC5F5F]/25"}`}
                data-testid="dispatch-status-chip">
                <Truck size={13} strokeWidth={2} /> {so.timeline[so.stage_index]}
              </span>
              {so.po_number && <span className="text-xs text-slate-500">Your PO: {so.po_number}</span>}
            </div>
            <div className="text-xs text-slate-500">Ordered: {fmtDate(so.order_date || so.created_at)}</div>
          </div>

          {/* tracking timeline */}
          <div className="mt-6 overflow-x-auto pb-1">
            <div className="flex items-start min-w-[720px]">
              {so.timeline.map((step, i) => {
                const Icon = STAGE_ICONS[step] || Check;
                const done = i < so.stage_index;
                const current = i === so.stage_index;
                const last = i === so.timeline.length - 1;
                return (
                  <div key={step} className="flex-1 flex flex-col items-center relative">
                    {i > 0 && <div className={`absolute top-[22px] -left-1/2 w-full h-[3px] rounded-full ${
                      i < so.stage_index ? "bg-gradient-to-r from-[#3B82F6] to-[#2b4a8f]"
                      : i === so.stage_index ? "track-line-flow"
                      : "bg-slate-200"}`} />}
                    <div className={`w-11 h-11 rounded-2xl flex items-center justify-center z-10 transition-all duration-300 ${
                      done ? "bg-[#3B82F6] text-white shadow-[0_4px_14px_rgba(59,130,246,0.35)]"
                      : current && last ? "bg-emerald-500 text-white shadow-[0_4px_14px_rgba(16,185,129,0.4)]"
                      : current ? "bg-[#FC5F5F] text-white track-current"
                      : "bg-white border-2 border-slate-200 text-slate-300"}`}>
                      <Icon size={18} strokeWidth={1.8} className={current && !last ? "track-current-icon" : ""} />
                    </div>
                    {done && (
                      <span className="absolute top-0 right-[calc(50%-26px)] w-4 h-4 rounded-full bg-emerald-500 text-white flex items-center justify-center z-20 border-2 border-white">
                        <Check size={9} strokeWidth={3.5} />
                      </span>
                    )}
                    <div className={`text-[10px] mt-2 text-center leading-tight px-1 ${current ? "text-[#FC5F5F] font-bold" : i <= so.stage_index ? "text-[#3B82F6] font-semibold" : "text-slate-300"}`}>{step}</div>
                    {current && <div className="text-[9px] text-slate-400 mt-0.5">{last ? "Completed" : "In progress"}</div>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* dispatch details — shown once dispatched */}
          {isDispatched && (
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <Detail l="LR / Docket Number" v={so.dispatch?.lr_number} />
              <Detail l="Transporter" v={so.dispatch?.transporter} />
              <Detail l="E-Way Bill Number" v={so.dispatch?.eway_bill_number} />
              <Detail l="Dispatch Date" v={so.dispatch?.dispatch_date ? fmtDate(so.dispatch.dispatch_date) : ""} />
              <Detail l="Invoice" v={so.dispatch?.invoice_number} />
            </div>
          )}
          {!isDispatched && so.expected_dispatch_date && (
            <div className="mt-4">
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2.5 py-1">
                <Truck size={11} /> Expected dispatch by {fmtDate(so.expected_dispatch_date)} (5–6 working days)
              </span>
            </div>
          )}

          {(so.dispatch?.track_url || so.dispatch?.tracking_token || so.dispatch?.pod_file_id) && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {(so.dispatch?.track_url || so.dispatch?.tracking_token) && (
              <a href={so.dispatch.track_url || `/track/${so.dispatch.tracking_token}`} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1.5 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-md px-5 py-2.5 text-xs font-semibold transition-colors duration-200"
                 data-testid={`pd-track-${so.id}`}>
                <Truck size={13} /> Track shipment live <ExternalLink size={11} />
              </a>
              )}
              {so.dispatch?.pod_file_id && (
              <button onClick={() => viewPod(so.dispatch.pod_file_id)}
                 className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-5 py-2.5 text-xs font-semibold transition-colors duration-200"
                 data-testid={`pd-pod-${so.id}`}>
                <FileCheck2 size={13} /> View Proof of Delivery
              </button>
              )}
            </div>
          )}
        </div>
        );
      })}
      {list !== null && tracked.length === 0 && (
        <p className="text-sm text-slate-400 py-8 text-center bg-white border border-slate-200 rounded-xl" data-testid="pd-empty">
          No orders to track yet — once an order is confirmed, its step-by-step tracking will appear here.
        </p>
      )}
    </div>
  );
}
